// Address utilities: IPv4, IPv6 and MAC helpers used by every engine.
// Pure functions only — no network state lives here.

export function ipToInt(s) {
  if (typeof s !== 'string' || !/^\d{1,3}(\.\d{1,3}){3}$/.test(s)) return null;
  const p = s.split('.').map(Number);
  if (p.some((x) => x > 255)) return null;
  return ((p[0] << 24) >>> 0) + (p[1] << 16) + (p[2] << 8) + p[3];
}
export const intToIp = (n) => [n >>> 24, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
export const isIp = (s) => ipToInt(s) !== null;

/** Returns prefix length for a contiguous mask, or null for an invalid mask. */
export function maskToPrefix(m) {
  const n = ipToInt(m);
  if (n === null) return null;
  let p = 0;
  while (p < 32 && ((n >>> (31 - p)) & 1)) p++;
  const rebuilt = p === 0 ? 0 : (0xffffffff << (32 - p)) >>> 0;
  return rebuilt === n ? p : null;
}
export const prefixToMask = (p) => intToIp(p === 0 ? 0 : (0xffffffff << (32 - p)) >>> 0);
export const prefixToWildcard = (p) => intToIp(~(p === 0 ? 0 : (0xffffffff << (32 - p))) >>> 0);

export function networkOf(ip, maskOrPrefix) {
  const m = typeof maskOrPrefix === 'number' ? ipToInt(prefixToMask(maskOrPrefix)) : ipToInt(maskOrPrefix);
  return intToIp((ipToInt(ip) & m) >>> 0);
}
export function broadcastOf(ip, maskOrPrefix) {
  const m = typeof maskOrPrefix === 'number' ? ipToInt(prefixToMask(maskOrPrefix)) : ipToInt(maskOrPrefix);
  return intToIp(((ipToInt(ip) & m) | (~m >>> 0)) >>> 0);
}
export function inSubnet(ip, net, maskOrPrefix) {
  if (!isIp(ip) || !isIp(net)) return false;
  return networkOf(ip, maskOrPrefix) === networkOf(net, maskOrPrefix);
}
/** True when `ip` is a usable host address (not network / broadcast) in its subnet. */
export function isHostAddress(ip, mask) {
  const p = maskToPrefix(mask);
  if (p === null) return false;
  if (p >= 31) return true;
  return ip !== networkOf(ip, mask) && ip !== broadcastOf(ip, mask);
}
export function classfulPrefix(ip) {
  const f = ipToInt(ip) >>> 24;
  if (f < 128) return 8;
  if (f < 192) return 16;
  return 24;
}
export const hostsForPrefix = (p) => (p >= 31 ? (p === 31 ? 2 : 1) : 2 ** (32 - p) - 2);
/** Wildcard (ACL) match: address matches base with wildcard bits ignored. */
export function wildcardMatch(ip, base, wild) {
  const w = ipToInt(wild);
  return ((ipToInt(ip) & ~w) >>> 0) === ((ipToInt(base) & ~w) >>> 0);
}

// ---------------------------------------------------------------- MAC
export const macCisco = (m) => {
  const h = m.replace(/[^0-9a-f]/gi, '').toLowerCase();
  return `${h.slice(0, 4)}.${h.slice(4, 8)}.${h.slice(8, 12)}`;
};
export const macWindows = (m) =>
  m.replace(/[^0-9a-f]/gi, '').toUpperCase().match(/../g).join('-');
export const macColon = (m) =>
  m.replace(/[^0-9a-f]/gi, '').toLowerCase().match(/../g).join(':');

// ---------------------------------------------------------------- IPv6
/** Expand an IPv6 address to 8 groups of 4 hex digits. Returns null when invalid. */
export function v6Expand(addr) {
  if (typeof addr !== 'string') return null;
  let a = addr.trim().toLowerCase().split('%')[0];
  if (!/^[0-9a-f:]+$/.test(a) || a.includes(':::')) return null;
  const dbl = a.split('::');
  if (dbl.length > 2) return null;
  const left = dbl[0] ? dbl[0].split(':') : [];
  const right = dbl.length === 2 && dbl[1] ? dbl[1].split(':') : [];
  if ([...left, ...right].some((g) => g.length === 0 || g.length > 4)) return null;
  let groups;
  if (dbl.length === 2) {
    const fill = 8 - left.length - right.length;
    if (fill < 1) return null;
    groups = [...left, ...Array(fill).fill('0'), ...right];
  } else groups = left;
  if (groups.length !== 8) return null;
  return groups.map((g) => g.padStart(4, '0')).join(':');
}
/** RFC 5952 canonical compression (leading zeros removed, longest zero run → ::). */
export function v6Compress(addr) {
  const full = v6Expand(addr);
  if (!full) return null;
  const g = full.split(':').map((x) => x.replace(/^0+(?=.)/, ''));
  let best = [-1, 0], cur = [-1, 0];
  g.forEach((x, i) => {
    if (x === '0') {
      if (cur[0] === -1) cur = [i, 1]; else cur[1]++;
      if (cur[1] > best[1]) best = [...cur];
    } else cur = [-1, 0];
  });
  if (best[1] < 2) return g.join(':');
  const l = g.slice(0, best[0]).join(':');
  const r = g.slice(best[0] + best[1]).join(':');
  return `${l}::${r}`;
}
/** Modified EUI-64 interface identifier from a MAC (flip U/L bit, insert FFFE). */
export function eui64(mac) {
  const h = mac.replace(/[^0-9a-f]/gi, '').toLowerCase();
  const first = (parseInt(h.slice(0, 2), 16) ^ 0x02).toString(16).padStart(2, '0');
  const x = first + h.slice(2, 6) + 'fffe' + h.slice(6);
  return x.match(/.{4}/g).join(':');
}
export const linkLocalFromMac = (mac) => v6Compress(`fe80:0:0:0:${eui64(mac)}`);
/** First 64 bits of a /64 prefix as 4 groups, e.g. "2001:0db8:acad:0010". */
export const v6Prefix64 = (addr) => v6Expand(addr)?.split(':').slice(0, 4).join(':');
